import apiClient from "@/api-client";
import { logger } from "@/lib/default-logger";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

export interface PhoneNumber {
	id: string;
	number: string;
	virtual?: boolean;
  enabled: boolean;
  campaign: {
    id: string;
    title: string;
  };
  concurrencyLimit: number;
  plugin: {
    id: string;
    title: string;
  };
  countryCode: string;
}

export interface PhoneNumbersResponse {
	data: PhoneNumber[];
	total: number;
}

export const phoneNumbersKeys = {
	all: ["phoneNumbers"] as const,
	lists: () => [...phoneNumbersKeys.all, "list"] as const,
	list: (query?: string) => [...phoneNumbersKeys.lists(), { query }] as const,
};

export function usePhoneNumbers(query?: string) {
	return useQuery({
		queryKey: phoneNumbersKeys.list(query),
		queryFn: async (): Promise<PhoneNumbersResponse> => {
			const queryString = query ? `?${query}` : "";
			const response = await apiClient.get(`/phone-numbers${queryString}`);
			return response.data;
		},
	});
}

export function useCreatePhoneNumber() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ data }: { data: any }) => {
			const response = await apiClient.post(`/phone-numbers`, data);
			return response.data;
		},
		onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumbersKeys.lists() });
			toast.success("Phone number created successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Phone number creation failed");
		},
	});
}

export function useUpdatePhoneNumber() {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async ({ id, data }: { id: string; data: any }) => {
			const response = await apiClient.put(`/phone-numbers/${id}`, data);
			return response.data;
		},
		onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: phoneNumbersKeys.lists() });
			toast.success("Phone number updated successfully");
		},
		onError: (error: any) => {
			logger.error(error);
			const errorMessage = error?.response?.data?.message;
			toast.error(Array.isArray(errorMessage) ? errorMessage[0] : errorMessage || "Phone number update failed");
		},
	});
}